import React, { useEffect, useMemo, useState } from 'react';
import { M, SectionLabel, FilterPill } from '../components/primitives.js';
import {
  canopusCommandService,
  type CanopusHistoricalCase,
  type CanopusPhaseTiming,
  type CanopusSIPeriod,
  type CanopusStaffStatus,
  type CanopusStep,
} from '../../../services/canopusCommand.js';
import type { InsightsSurgeon } from '../../../services/insights.js';
// synthesized historical cases (already normalized to the
// cleaner demo specialty labels).
import { getDemoHistoricalCases } from '../data/demo-data.js';
import './historical.css';

interface HistoricalRow {
  id: string;
  date: string;
  surgeon: string;
  specialty: string;
  procedure: string;
  or: string;
  duration: number;
  avgDur: number | null;
  caseNum: number;
  profileName: string | null;
  startTimeIso: string;
  intraoperativeStartTimeIso: string | null;
  intraoperativeEndTimeIso: string | null;
  phases: CanopusPhaseTiming[];
  steps: CanopusStep[];
  scrubTechName: string | null;
  circNurseName: string | null;
  deviceName: string | null;
}

interface PhaseBreakdownEntry {
  p: string;
  dur: string;
  steps: number;
}

interface StepEntry {
  name: string;
  phase: string;
  time: string;
  sourceLabel: string;
}

interface CaseDetail {
  profile: string;
  startTime: string;
  endTime: string;
  totalSteps: number;
  phaseBreakdown: PhaseBreakdownEntry[];
  steps: StepEntry[];
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const h = d.getHours() % 12 || 12;
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = d.getHours() < 12 ? 'AM' : 'PM';
  return `${h}:${m} ${ampm}`;
}

function fmtDurSec(sec: number | null): string {
  if (sec === null || sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m}m`;
}

function buildCaseDetail(c: HistoricalRow): CaseDetail | null {
  const hasPhases = c.phases && c.phases.length > 0;
  const hasSteps = c.steps && c.steps.length > 0;
  if (!hasPhases && !hasSteps) return null;

  const completedSteps = (c.steps ?? [])
    .filter((s) => s.done)
    .sort((a, b) => {
      const at = a.clickTimeIso ? new Date(a.clickTimeIso).getTime() : Number.POSITIVE_INFINITY;
      const bt = b.clickTimeIso ? new Date(b.clickTimeIso).getTime() : Number.POSITIVE_INFINITY;
      if (at !== bt) return at - bt;
      return a.name.localeCompare(b.name);
    });

  const phaseBreakdown: PhaseBreakdownEntry[] = hasPhases
    ? c.phases.map((p) => ({
        p: p.name,
        dur: fmtDurSec(p.durationSec),
        steps: hasSteps ? completedSteps.filter((s) => s.phase === p.name).length : 0,
      }))
    : [];

  const steps: StepEntry[] = completedSteps.map((s) => ({
    name: s.name,
    phase: s.phase || `Phase ${s.phaseIndex + 1}`,
    time: fmtTime(s.clickTimeIso),
    sourceLabel: s.source === 1 ? 'Intra-op' : s.source === 0 ? 'Pre-op' : 'Unknown source',
  }));

  return {
    profile: c.profileName ?? '—',
    startTime: fmtTime(c.intraoperativeStartTimeIso ?? c.startTimeIso),
    endTime: fmtTime(c.intraoperativeEndTimeIso),
    totalSteps: steps.length,
    phaseBreakdown,
    steps,
  };
}

// ─── CaseDetailPanel ─ per-case drawer (mockup line 4396) ─────────
function CaseDetailPanel({ c, onClose }: { c: HistoricalRow; onClose: () => void }) {
  const detail = useMemo(() => buildCaseDetail(c), [c.id]);
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);
  const fmtDur = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m}m`;
  };
  if (!detail)
    return (
      <div className="hist-detail">
        <div className="hist-detail-empty-header">
          <span className="hist-detail-empty-title">Case Detail — {c.procedure}</span>
          <button onClick={onClose} className="hist-detail-close">
            Close
          </button>
        </div>
        <div className="hist-detail-empty-msg">
          Detailed step data not available for this procedure type.
        </div>
      </div>
    );
  return (
    <div className="hist-detail">
      <div className="hist-detail-header">
        <div>
          <div className="hist-title-row">
            <span className="hist-procedure-name">{c.procedure}</span>
            <span className="hist-completed-badge">COMPLETED</span>
          </div>
          <div className="hist-procedure-meta">
            {c.surgeon} {'•'} {c.date} {'•'} {c.or}
          </div>
        </div>
        <button onClick={onClose} className="hist-detail-close">
          Close
        </button>
      </div>
      <div className="hist-summary-row">
        <div className="hist-summary-tile">
          <div className="hist-summary-label">Profile</div>
          <div className="hist-summary-val">{detail.profile}</div>
        </div>
        <div className="hist-summary-tile metric-tip">
          <div className="hist-summary-label">Staff</div>
          {c.scrubTechName || c.circNurseName ? (
            <div className="hist-summary-val">
              {c.scrubTechName ? (
                <div className="hist-summary-staff-line">
                  <span className="hist-summary-staff-role">ST</span> {c.scrubTechName}
                </div>
              ) : null}
              {c.circNurseName ? (
                <div className="hist-summary-staff-line">
                  <span className="hist-summary-staff-role">CN</span> {c.circNurseName}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="hist-summary-val--placeholder">Role data not yet available</div>
          )}
          <span className="metric-tip-text">
            Scrub Tech (ST) and Circulating Nurse (CN) recorded in the procedure record&apos;s
            currentParticipants for this case.
          </span>
        </div>
        <div className="hist-summary-tile">
          <div className="hist-summary-label">Duration</div>
          <div className="hist-summary-val--big">{fmtDur(c.duration)}</div>
          {c.avgDur && (
            <div className="hist-summary-sub">
              avg: {fmtDur(c.avgDur)} ({c.caseNum} cases)
            </div>
          )}
        </div>
        <div className="hist-summary-tile">
          <div className="hist-summary-label">Variance</div>
          {c.avgDur ? (
            <>
              {/* color flips with sign of variance (over = warning, under/zero = accent). */}
              <div
                className="hist-summary-val--big"
                style={{
                  color: c.duration - c.avgDur > 0 ? '#FBBF24' : '#0FB7D1',
                }}
              >
                {c.duration - c.avgDur > 0 ? '+' : ''}
                {c.duration - c.avgDur}m
              </div>
              <div className="hist-summary-sub">vs baseline ({c.caseNum} cases)</div>
            </>
          ) : (
            <div className="hist-summary-val--building">Building</div>
          )}
        </div>
        <div className="hist-summary-tile metric-tip">
          <div className="hist-summary-label">Device</div>
          <div className={c.deviceName ? 'hist-summary-val' : 'hist-summary-val--device'}>
            {c.deviceName ?? '—'}
          </div>
          <span className="metric-tip-text">
            Recording device used during this case (e.g. iPad, Apple Vision Pro).
          </span>
        </div>
        <div className="hist-summary-tile">
          <div className="hist-summary-label">Checked Steps</div>
          <div className="hist-summary-val--big">{detail.totalSteps}</div>
          <div className="hist-summary-sub">
            {detail.startTime} — {detail.endTime}
          </div>
        </div>
      </div>
      <div className="hist-detail-grid">
        <div>
          <SectionLabel text="Phase Timeline (click to expand steps)" />
          {detail.phaseBreakdown.map((phase, i) => {
            const isExpanded = expandedPhase === i;
            const phaseSteps = detail.steps.filter((s) => s.phase === phase.p);
            return (
              <div key={i}>
                <div
                  onClick={() => setExpandedPhase(isExpanded ? null : i)}
                  className={`hist-phase-row ${isExpanded ? 'hist-phase-row--expanded' : ''}`}
                >
                  <div className="hist-phase-num">{i + 1}</div>
                  <div className="hist-phase-body">
                    <div className="hist-phase-name">{phase.p}</div>
                    <div className="hist-phase-steps-count">{phase.steps} steps</div>
                  </div>
                  <span className="hist-phase-dur">{phase.dur}</span>
                  <span
                    className={`hist-phase-caret ${isExpanded ? 'hist-phase-caret--expanded' : ''}`}
                  >
                    {'▶'}
                  </span>
                </div>
                {isExpanded && (
                  <div className="hist-phase-expanded-body">
                    {phaseSteps.map((step, si) => (
                      <div
                        key={si}
                        className={`hist-step-row ${
                          si < phaseSteps.length - 1 ? 'hist-step-row--with-border' : ''
                        }`}
                      >
                        <div className="hist-step-check">
                          <span className="hist-step-check-ico">{'✓'}</span>
                        </div>
                        <span className="hist-step-name">{step.name}</span>
                        <span className="hist-step-time">{step.time}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div>
          <SectionLabel text="Checked Steps (Chronological)" />
          <div className="hist-timeline">
            {detail.steps.map((step, si) => (
              <div key={si} className="hist-timeline-phase">
                <div className="hist-timeline-phase-name">
                  {step.phase} · {step.sourceLabel}
                </div>
                <div className="hist-timeline-step">
                  <span className="hist-timeline-time">{step.time}</span>
                  <div className="hist-timeline-dot" />
                  <span className="hist-timeline-name">{step.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HistoricalTab ─ Historical Cases tab (mockup line 4847) ──────
export function HistoricalTab({
  facilityId,
  /** UK facilities use 'OT' (Operating Theatre); everywhere else 'OR'. */
  roomLabel = 'OR',
  // when true, raw specialty strings are remapped onto
  // the cleaner demo labels (and merged where they collapse together) and
  // the historical cases themselves are replaced with synthesized demo
  // rows so the table stays populated even when real data is sparse.
  demoMode = false,
  // real surgeon list used to label demo rows. Optional;
  // falls back to "Dr. Demo N" placeholder names when null/empty.
  realSurgeons = null,
  // real staff list used to fill each demo row's Scrub
  // Tech / Circ Nurse fields. Optional; falls back to placeholder names.
  realStaff = null,
}: {
  facilityId: string | null;
  roomLabel?: string;
  demoMode?: boolean;
  realSurgeons?: InsightsSurgeon[] | null;
  realStaff?: CanopusStaffStatus | null;
}) {
  const [period, setPeriod] = useState<CanopusSIPeriod>('all');
  const [surgeon, setSurgeon] = useState<string>('all');
  const [realCases, setRealCases] = useState<HistoricalRow[]>([]);
  const [isLoading, setIsLoading] = useState(!!facilityId);

  const [specialty, setSpecialty] = useState<string>('all');
  const [procedure, setProcedure] = useState<string>('all');
  const [room, setRoom] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selectedCase, setSelectedCase] = useState<HistoricalRow | null>(null);
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState<string>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const PAGE_SIZE = 25;

  useEffect(() => {
    if (!facilityId) {
      setRealCases([]);
      setIsLoading(false);
      return;
    }
    // Local helper — same row shape, just keeps the map() inline below
    // readable for both the demo and live branches.
    const mapToRow = (c: CanopusHistoricalCase): HistoricalRow => ({
      id: c.procedureId,
      date: c.dateIso,
      surgeon: c.surgeonName,
      specialty: c.specialty ?? '',
      procedure: c.procedureName,
      or: c.roomName,
      duration: Math.round(c.totalTimeSec / 60),
      avgDur: c.avgDurationSec !== null ? Math.round(c.avgDurationSec / 60) : null,
      caseNum: c.caseCount,
      profileName: c.profileName,
      startTimeIso: c.startTimeIso,
      intraoperativeStartTimeIso: c.intraoperativeStartTimeIso,
      intraoperativeEndTimeIso: c.intraoperativeEndTimeIso,
      phases: c.phases,
      steps: c.steps,
      scrubTechName: c.scrubTechName,
      circNurseName: c.circNurseName,
      deviceName: c.deviceName,
    });

    // short-circuit the network fetch — synthesize a
    // populated case list directly. Real data is often incomplete for
    // demo facilities, so we always show the demo dataset here.
    if (demoMode) {
      setIsLoading(false);
      setRealCases(
        getDemoHistoricalCases(period, realSurgeons, realStaff?.members ?? null).map(mapToRow)
      );
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    canopusCommandService
      .getHistoricalCases(facilityId, period)
      .then((result) => {
        if (cancelled) return;
        const cases = result ? result.cases : [];
        setRealCases(cases.map(mapToRow));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId, period, demoMode, realSurgeons, realStaff]);

  const cases = realCases;
  const surgeons = useMemo(() => [...new Set(cases.map((c) => c.surgeon))].sort(), [cases]);
  const specialties = useMemo(
    () => [...new Set(cases.map((c) => c.specialty).filter(Boolean))].sort(),
    [cases]
  );
  const procedures = useMemo(() => [...new Set(cases.map((c) => c.procedure))].sort(), [cases]);
  const rooms = useMemo(() => [...new Set(cases.map((c) => c.or).filter(Boolean))].sort(), [cases]);
  const cutoff = useMemo<Date | null>(() => {
    const now = new Date();
    if (period === '7d') return new Date(now.getTime() - 7 * 86400000);
    if (period === '30d') return new Date(now.getTime() - 30 * 86400000);
    if (period === '90d') return new Date(now.getTime() - 90 * 86400000);
    if (period === 'ytd') return new Date(`${new Date().getFullYear()}-01-01`);
    return null;
  }, [period]);
  useEffect(() => {
    setSelectedCase(null);
    setPage(0);
    setSortCol('date');
    setSortDir('desc');
  }, [surgeon, specialty, procedure, room, search, cutoff]);
  const filtered = useMemo(() => {
    return cases.filter((c) => {
      if (cutoff && new Date(c.date) < cutoff) return false;
      if (surgeon !== 'all' && c.surgeon !== surgeon) return false;
      if (specialty !== 'all' && c.specialty !== specialty) return false;
      if (procedure !== 'all' && c.procedure !== procedure) return false;
      if (room !== 'all' && c.or !== room) return false;
      if (
        search &&
        !c.procedure.toLowerCase().includes(search.toLowerCase()) &&
        !c.surgeon.toLowerCase().includes(search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [cases, cutoff, surgeon, specialty, procedure, room, search]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av: string | number;
      let bv: string | number;
      switch (sortCol) {
        case 'date':
          av = a.date;
          bv = b.date;
          break;
        case 'surgeon':
          av = a.surgeon;
          bv = b.surgeon;
          break;
        case 'specialty':
          av = a.specialty;
          bv = b.specialty;
          break;
        case 'procedure':
          av = a.procedure;
          bv = b.procedure;
          break;
        case 'or':
          av = a.or;
          bv = b.or;
          break;
        case 'duration':
          av = a.duration;
          bv = b.duration;
          break;
        case 'variance':
          av = a.avgDur != null ? a.duration - a.avgDur : Infinity;
          bv = b.avgDur != null ? b.duration - b.avgDur : Infinity;
          break;
        default:
          return 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortCol, sortDir]);
  const handleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'date' ? 'desc' : 'asc');
    }
    setPage(0);
  };
  const stats = useMemo(() => {
    if (filtered.length === 0)
      return {
        count: 0,
        avgDur: '--',
        avgVariance: '--',
        avgVarNum: 0,
        longRate: '--',
      };
    const avgD = Math.round(filtered.reduce((s, c) => s + c.duration, 0) / filtered.length);
    const withBaseline = filtered.filter(
      (c): c is HistoricalRow & { avgDur: number } => c.avgDur != null
    );
    const avgVar =
      withBaseline.length > 0
        ? (
            withBaseline.reduce((s, c) => s + (c.duration - c.avgDur), 0) / withBaseline.length
          ).toFixed(1)
        : null;
    const longCount = filtered.filter(
      (c) => c.avgDur != null && c.duration > c.avgDur * 1.1
    ).length;
    const longRate = Math.round((longCount / filtered.length) * 100);
    const h = Math.floor(avgD / 60);
    const m = avgD % 60;
    const avgVarNum = avgVar != null ? parseFloat(avgVar) : 0;
    const varStr = avgVar !== null ? `${avgVarNum > 0 ? '+' : ''}${avgVar}m` : '--';
    return {
      count: filtered.length,
      avgDur: `${h}h ${m}m`,
      avgVariance: varStr,
      avgVarNum,
      longRate: `${longRate}%`,
    };
  }, [filtered]);
  const fmtDur = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };
  return (
    <div>
      <div className="hist-info-banner">
        All case data from reference guide check-off recordings. Click any case to view the full
        phase and step-by-step breakdown with timestamps.
      </div>
      <div className="hist-toolbar">
        <input
          type="text"
          placeholder="Search surgeon or procedure..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="hist-search"
        />
        <FilterPill
          label="Period"
          value={period}
          onChange={(v: string) => setPeriod(v as CanopusSIPeriod)}
          options={[
            { v: 'all', l: 'All Time' },
            { v: 'ytd', l: 'Year to Date' },
            { v: '90d', l: 'Last 90 Days' },
            { v: '30d', l: 'Last 30 Days' },
          ]}
        />
        <FilterPill
          label="Surgeon"
          value={surgeon}
          onChange={setSurgeon}
          options={[{ v: 'all', l: 'All' }, ...surgeons.map((s) => ({ v: s, l: s }))]}
        />
        <FilterPill
          label="Specialty"
          value={specialty}
          onChange={setSpecialty}
          options={[{ v: 'all', l: 'All' }, ...specialties.map((s) => ({ v: s, l: s }))]}
        />
        <FilterPill
          label="Procedure"
          value={procedure}
          onChange={setProcedure}
          options={[{ v: 'all', l: 'All' }, ...procedures.map((s) => ({ v: s, l: s }))]}
        />
        <FilterPill
          label={roomLabel}
          value={room}
          onChange={setRoom}
          options={[{ v: 'all', l: 'All' }, ...rooms.map((s) => ({ v: s, l: s }))]}
        />
      </div>
      <div className="hist-metrics">
        <M
          label="Matching Cases"
          value={stats.count}
          sub={
            period === 'all'
              ? 'All time'
              : period === 'ytd'
                ? '2026 YTD'
                : period === '90d'
                  ? 'Last 90 days'
                  : 'Last 30 days'
          }
          loading={isLoading}
        />
        <M label="Avg Duration" value={stats.avgDur} sub="Recording time" loading={isLoading} />
        <M
          label="Avg Variance"
          value={stats.avgVariance}
          sub="vs surgeon-procedure baseline"
          color={stats.avgVarNum <= 0 ? '#0FB7D1' : '#FBBF24'}
          loading={isLoading}
        />
        <M
          label="% Running Long"
          value={stats.longRate}
          sub="> 10% over historical avg"
          color={parseInt(stats.longRate) > 25 ? '#FFB300' : '#FBBF24'}
          loading={isLoading}
        />
      </div>
      <div className="hist-section">
        <SectionLabel
          text={`Case Records (${filtered.length} cases) — Click a row to view full step breakdown`}
        />
        <table className="hist-table">
          <thead>
            <tr>
              {[
                { key: '', label: '' },
                { key: 'date', label: 'Date' },
                { key: 'surgeon', label: 'Surgeon' },
                { key: 'specialty', label: 'Specialty' },
                { key: 'procedure', label: 'Procedure' },
                { key: 'or', label: roomLabel },
                { key: 'duration', label: 'Duration' },
                { key: 'variance', label: 'Variance' },
              ].map((col) => (
                <th
                  key={col.label}
                  onClick={col.key ? () => handleSort(col.key) : undefined}
                  className={`hist-th ${col.key ? 'hist-th--sortable' : ''}`}
                >
                  {col.label}
                  {col.key && sortCol === col.key && (
                    <span className="hist-sort-arrow">{sortDir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && cases.length === 0
              ? Array.from({ length: 15 }).map((_, i) => (
                  <tr key={`skel-${i}`} className="hist-row-skel">
                    <td className="hist-skel-toggle-cell">
                      <div className="hist-skel-box loading-blur" />
                    </td>
                    <td className="loading-blur hist-td hist-td--date">Jan 1, 2026</td>
                    <td className="loading-blur hist-td hist-td--surgeon">Surgeon Name</td>
                    <td className="loading-blur hist-td hist-td--specialty">Specialty</td>
                    <td className="loading-blur hist-td hist-td--procedure">Procedure Name</td>
                    <td className="loading-blur hist-td hist-td--or">OR 1</td>
                    <td className="loading-blur hist-td hist-td--tech">Tech</td>
                    <td className="loading-blur hist-td hist-td--duration">0h 00m</td>
                    {/* Variance value color is data-driven; placeholder uses accent. */}
                    <td className="loading-blur hist-td" style={{ color: '#0FB7D1' }}>
                      +0m
                    </td>
                  </tr>
                ))
              : sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((c, i) => {
                  const diff = c.avgDur ? c.duration - c.avgDur : null;
                  const diffStr = diff !== null ? `${diff > 0 ? '+' : ''}${diff}m` : 'Building';
                  const diffColor = diff === null ? '#6366F1' : diff > 0 ? '#FBBF24' : '#0FB7D1';
                  const isSel = selectedCase && selectedCase.id === c.id;
                  return (
                    <React.Fragment key={i}>
                      <tr
                        onClick={() => setSelectedCase(isSel ? null : c)}
                        className={`hist-row ${isSel ? 'hist-row--sel' : ''}`}
                        onMouseOver={(e) => {
                          if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                        }}
                        onMouseOut={(e) => {
                          if (!isSel) e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <td className="hist-toggle-cell">
                          <div className={`hist-toggle-box ${isSel ? 'hist-toggle-box--sel' : ''}`}>
                            <span
                              className={`hist-toggle-icon ${isSel ? 'hist-toggle-icon--sel' : ''}`}
                            >
                              {isSel ? '✕' : '▶'}
                            </span>
                          </div>
                        </td>
                        <td className="hist-td hist-td--date">{c.date}</td>
                        <td className="hist-td hist-td--surgeon">{c.surgeon}</td>
                        <td className="hist-td hist-td--specialty">{c.specialty}</td>
                        <td
                          className={`hist-td ${
                            isSel ? 'hist-td--procedure-sel' : 'hist-td--procedure'
                          }`}
                        >
                          {c.procedure}
                        </td>
                        <td className="hist-td hist-td--or">{c.or}</td>
                        <td className="hist-td hist-td--duration">{fmtDur(c.duration)}</td>
                        {/* Variance color is dynamic per-row (sign of diff). */}
                        <td className="hist-td" style={{ color: diffColor, fontWeight: 500 }}>
                          {diffStr}
                        </td>
                      </tr>
                      {isSel && (
                        <tr>
                          <td colSpan={8} className="hist-detail-cell">
                            <CaseDetailPanel c={c} onClose={() => setSelectedCase(null)} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
          </tbody>
        </table>
        {(() => {
          const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
          return totalPages > 1 ? (
            <div className="hist-pagination">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className={`hist-page-btn ${page === 0 ? 'hist-page-btn--disabled' : ''}`}
              >
                Previous
              </button>
              <span className="hist-page-info">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)}{' '}
                of {filtered.length}
              </span>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                className={`hist-page-btn ${
                  page >= totalPages - 1 ? 'hist-page-btn--disabled' : ''
                }`}
              >
                Next
              </button>
            </div>
          ) : null;
        })()}
        {!isLoading && filtered.length === 0 && (
          <div className="hist-empty">No cases match the selected filters.</div>
        )}
      </div>
    </div>
  );
}
